export const FEED_STALE_TIME = 600000;
export const TAXONOMY_STALE_TIME = 3600000;

const ERROR_COOLDOWN = 15000;

const cache = new Map();
const listeners = new Set();

let version = 0;

const notify = () => {
  version += 1;
  listeners.forEach((listener) => listener());
};

export const subscribeToCache = (listener) => {
  listeners.add(listener);

  return () => listeners.delete(listener);
};

export const getCacheVersion = () => version;

export const getEntry = (key) => (key ? cache.get(key) : undefined);

const isAbortError = (error) =>
  error?.name === "CanceledError" ||
  error?.name === "AbortError" ||
  error?.code === "ERR_CANCELED";

const isFresh = (entry, staleTime) =>
  Boolean(entry) && entry.status === "success" && Date.now() - entry.at < staleTime;

const isCoolingDown = (entry) =>
  Boolean(entry) &&
  entry.status === "error" &&
  Date.now() - entry.failedAt < ERROR_COOLDOWN;

export const runQuery = ({
  key,
  tag,
  fetcher,
  staleTime = FEED_STALE_TIME,
  force = false,
}) => {
  if (!key) return Promise.resolve(undefined);

  const existing = cache.get(key);

  if (existing?.promise) return existing.promise;
  if (!force && isFresh(existing, staleTime)) return Promise.resolve(existing.data);
  if (!force && isCoolingDown(existing)) return Promise.reject(existing.error);

  const entry = existing || {
    tag,
    data: undefined,
    error: null,
    status: "idle",
    at: 0,
    failedAt: 0,
  };

  entry.tag = tag;
  entry.promise = Promise.resolve()
    .then(() => fetcher())
    .then((data) => {
      entry.data = data;
      entry.error = null;
      entry.status = "success";
      entry.at = Date.now();

      return data;
    })
    .catch((error) => {
      if (!isAbortError(error)) {
        entry.error = error;
        entry.status = "error";
        entry.failedAt = Date.now();
      }

      throw error;
    })
    .finally(() => {
      entry.promise = null;
      notify();
    });

  cache.set(key, entry);
  notify();

  return entry.promise;
};

export const updateEntryData = (key, updater) => {
  const entry = getEntry(key);
  if (!entry || entry.data === undefined) return false;

  const next = updater(entry.data);
  if (next === undefined || next === entry.data) return false;

  entry.data = next;
  notify();

  return true;
};

export const invalidateTags = (tags) => {
  const targets = new Set([].concat(tags || []));
  if (!targets.size) return;

  cache.forEach((entry) => {
    if (targets.has(entry.tag)) {
      entry.at = 0;
      entry.failedAt = 0;
      if (entry.status === "error") entry.status = "idle";
    }
  });

  notify();
};

export const hasInFlight = (tags) => {
  const targets = new Set([].concat(tags || []));

  for (const entry of cache.values()) {
    if (entry.promise && targets.has(entry.tag)) return true;
  }

  return false;
};

export const clearCache = () => {
  cache.clear();
  notify();
};
